// Helpers for the Conferences panel.
//
// Data shape (all additive / optional so partial "placeholder" editions are
// valid). One card = one conference; `entries` is the per-year marquee.
//
//   card: { id, title, organizers, titleExpanded, entries: Entry[], order, createdAt, updatedAt }
//   entry: {
//     id, year, dateFrom, dateTo, venue, website,
//     paperName, paperType, paperTypeOther,
//     submissionDeadline,            // the single deadline -> Reminders
//     authors, details,             // details = markdown string
//     theme, fields,                // fields = free-text tags (academic areas)
//     live,                         // boolean (default true)
//     submitted,                    // boolean
//     remember,                     // boolean -> Remember panel (toggleable)
//     decisionDate,                 // string|null -> Remember when pending
//     outcome,                      // 'pending' | 'accepted' | 'rejected'
//     statusOverride                // null | one of STATUS keys (manual override)
//   }
//
// All date math uses local "YYYY-MM-DD" strings to avoid timezone drift.

import { formatDate } from "./dateUtils";

export const PAPER_TYPES = [
  { id: "full", label: "Full Paper" },
  { id: "short", label: "Short Paper" },
  { id: "late", label: "Late Paper" },
  { id: "poster", label: "Poster" },
  { id: "competition", label: "Competition" },
  { id: "workshop", label: "Workshop" },
  { id: "other", label: "Other" },
];

export const paperTypeLabel = (entry) => {
  if (!entry) return "";
  if (entry.paperType === "other") {
    return (entry.paperTypeOther || "").trim() || "Other";
  }
  const t = PAPER_TYPES.find((p) => p.id === entry.paperType);
  return t ? t.label : "";
};

// Status lifecycle keys + presentation. Order matters for the manual dropdown.
export const STATUS = {
  start: { key: "start", label: "Start", color: "text-gray-300", badge: "bg-gray-500/20 text-gray-300 border-gray-500/40" },
  progress: { key: "progress", label: "In Progress", color: "text-yellow-300", badge: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40" },
  submitted: { key: "submitted", label: "Submitted", color: "text-blue-300", badge: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
  awaiting: { key: "awaiting", label: "Awaiting Decision", color: "text-indigo-300", badge: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40" },
  accepted: { key: "accepted", label: "Accepted", color: "text-green-300", badge: "bg-green-500/20 text-green-300 border-green-500/40" },
  rejected: { key: "rejected", label: "Rejected", color: "text-red-300", badge: "bg-red-500/20 text-red-300 border-red-500/40" },
  missed: { key: "missed", label: "Missed Deadline", color: "text-red-300", badge: "bg-red-500/20 text-red-300 border-red-500/40" },
  dead: { key: "dead", label: "Dead", color: "text-gray-400", badge: "bg-gray-600/30 text-gray-400 border-gray-600/50" },
};

// Statuses the user is allowed to pick manually (override). The rest are
// derived only.
export const MANUAL_STATUS_KEYS = ["start", "progress", "submitted", "awaiting", "accepted", "rejected"];

const parseLocal = (str) => {
  if (!str) return null;
  const [y, m, d] = String(str).split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export const todayStr = () => formatDate(new Date());

let _idCounter = 0;
const uid = () => Date.now() + Math.floor(Math.random() * 100000) + _idCounter++;
// Collision-proof id for a syncable record (the conference card itself). Nested
// entry ids keep using uid() — entries merge as part of their parent card.
const safeCardId = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {
    /* ignore */
  }
  return "id-" + Date.now().toString(36) + "-" + Math.floor(Math.random() * 1e9).toString(36);
};

export const newEntry = (year) => ({
  id: uid(),
  year: year != null ? String(year) : String(new Date().getFullYear()),
  dateFrom: null,
  dateTo: null,
  venue: "",
  website: "",
  paperName: "",
  paperType: "full",
  paperTypeOther: "",
  submissionDeadline: null,
  authors: "",
  details: "",
  theme: "",
  fields: [],
  live: true,
  submitted: false,
  remember: false,
  decisionDate: null,
  outcome: "pending",
  statusOverride: null,
});

export const newConferenceCard = (title = "") => ({
  id: safeCardId(),
  title: title || "Untitled Conference",
  organizers: "",
  titleExpanded: "",
  entries: [newEntry(new Date().getFullYear())],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

// An entry counts as "effectively empty" (a placeholder edition) when it has no
// deadline, no dates and no paper details. Used to auto-suggest Dead.
export const isEntryEmpty = (entry) => {
  if (!entry) return true;
  return (
    !entry.submissionDeadline &&
    !entry.dateFrom &&
    !entry.dateTo &&
    !(entry.paperName || "").trim() &&
    !(entry.venue || "").trim() &&
    !(entry.details || "").trim()
  );
};

export const isEntryLive = (entry) => !!entry && entry.live !== false;

// Derive a status key for an entry. Manual override wins. Otherwise: dead ->
// outcome -> submitted -> missed (past deadline, not submitted) -> progress ->
// start. Closes the "missed deadline" loophole automatically.
export const deriveEntryStatus = (entry, now = todayStr()) => {
  if (!entry) return STATUS.start;
  if (entry.statusOverride && STATUS[entry.statusOverride]) {
    return STATUS[entry.statusOverride];
  }
  if (!isEntryLive(entry)) return STATUS.dead;
  if (entry.outcome === "accepted") return STATUS.accepted;
  if (entry.outcome === "rejected") return STATUS.rejected;
  if (entry.submitted) return STATUS.awaiting;
  if (entry.submissionDeadline) {
    if (entry.submissionDeadline < now) return STATUS.missed;
    return STATUS.progress;
  }
  if (!isEntryEmpty(entry)) return STATUS.progress;
  return STATUS.start;
};

// A card is dead when it has no live entry.
export const cardIsDead = (card) =>
  !card || !(card.entries || []).some((e) => isEntryLive(e));

// Display order for status section headers in the Conferences panel.
export const STATUS_GROUP_ORDER = [
  "start",
  "progress",
  "submitted",
  "awaiting",
  "missed",
  "accepted",
  "rejected",
  "dead",
];

// Status for grouping / header badge: uses the marquee edition the user is on.
export const getCardDisplayStatus = (card, entryIndex = 0) => {
  if (cardIsDead(card)) return STATUS.dead;
  const entries = card.entries || [];
  const len = entries.length;
  const idx = Math.min(Math.max(entryIndex, 0), Math.max(len - 1, 0));
  const entry = entries[idx];
  return deriveEntryStatus(entry);
};

// Bucket sorted cards into status groups, preserving order within each group.
export const groupCardsByStatus = (cards, entryIndexByCardId = {}) => {
  const buckets = new Map();
  for (const card of cards || []) {
    const rawIdx = entryIndexByCardId[card.id] ?? 0;
    const key = getCardDisplayStatus(card, rawIdx).key;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(card);
  }
  return STATUS_GROUP_ORDER.filter(
    (key) => buckets.has(key) && buckets.get(key).length > 0
  ).map((key) => ({
    status: STATUS[key],
    cards: buckets.get(key),
  }));
};

// The reference date used for sorting an entry, by mode.
const entryRefDate = (entry, mode) => {
  if (mode === "date") return entry.dateFrom || entry.submissionDeadline || null;
  // deadline (default) and livedead use the submission deadline first.
  return entry.submissionDeadline || entry.dateFrom || null;
};

// Combined sort key for an entry's field tags (alphabetical join).
export const entryFieldSortKey = (entry) => {
  const tags = (entry?.fields || []).map((t) => String(t || "").trim()).filter(Boolean);
  if (tags.length === 0) return "";
  return [...tags].sort((a, b) => a.localeCompare(b)).join(", ");
};

// Pick the same representative live edition used for date sorting.
export const getRepresentativeEntry = (card, mode = "deadline") => {
  const todayMs = parseLocal(todayStr()).getTime();
  let bestUpcoming = null;
  let bestUpcomingMs = Infinity;
  let bestPast = null;
  let bestPastMs = -Infinity;
  for (const entry of card?.entries || []) {
    if (!isEntryLive(entry)) continue;
    const ref = entryRefDate(entry, mode);
    const parsed = parseLocal(ref);
    if (!parsed) continue;
    const ms = parsed.getTime();
    if (ms >= todayMs && ms < bestUpcomingMs) {
      bestUpcoming = entry;
      bestUpcomingMs = ms;
    } else if (ms < todayMs && ms > bestPastMs) {
      bestPast = entry;
      bestPastMs = ms;
    }
  }
  return bestUpcoming || bestPast || (card?.entries || []).find((e) => isEntryLive(e)) || null;
};

export const cardFieldSortKey = (card) => {
  const entry = getRepresentativeEntry(card, "deadline");
  return entry ? entryFieldSortKey(entry) : "";
};

export const collectAllFieldTags = (cards) => {
  const set = new Set();
  for (const card of cards || []) {
    for (const entry of card.entries || []) {
      for (const tag of entry.fields || []) {
        const trimmed = String(tag || "").trim();
        if (trimmed) set.add(trimmed);
      }
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
};

// Build a sortable representative key for a card: prefer the soonest UPCOMING
// edition (>= today); fall back to the most recent past edition; cards with no
// dates sort last (but above dead cards). Returns { bucket, ms }.
const representativeKey = (card, mode) => {
  const todayMs = parseLocal(todayStr()).getTime();
  let bestUpcoming = Infinity;
  let bestPast = -Infinity;
  for (const entry of card.entries || []) {
    if (!isEntryLive(entry)) continue;
    const ref = entryRefDate(entry, mode);
    const parsed = parseLocal(ref);
    if (!parsed) continue;
    const ms = parsed.getTime();
    if (ms >= todayMs) bestUpcoming = Math.min(bestUpcoming, ms);
    else bestPast = Math.max(bestPast, ms);
  }
  if (bestUpcoming !== Infinity) return { bucket: 0, ms: bestUpcoming };
  if (bestPast !== -Infinity) return { bucket: 1, ms: -bestPast };
  return { bucket: 2, ms: Infinity };
};

const cardTimestamp = (card, field) => {
  const raw = card?.[field];
  if (!raw) return 0;
  const ms = Date.parse(String(raw));
  return Number.isNaN(ms) ? 0 : ms;
};

// Sort cards for display. Dead cards always sink to the bottom. Within the live
// group, sort by the chosen mode (nearest upcoming first).
export const sortCards = (cards, mode = "deadline") => {
  const list = [...(cards || [])];
  return list.sort((a, b) => {
    const aDead = cardIsDead(a);
    const bDead = cardIsDead(b);
    if (aDead !== bDead) return aDead ? 1 : -1;
    if (mode === "added" || mode === "modified") {
      const field = mode === "added" ? "createdAt" : "updatedAt";
      const diff = cardTimestamp(b, field) - cardTimestamp(a, field);
      if (diff !== 0) return diff;
      return (a.title || "").localeCompare(b.title || "");
    }
    if (aDead && bDead) {
      // Both dead: keep stable-ish by title.
      return (a.title || "").localeCompare(b.title || "");
    }
    if (mode === "livedead") {
      // Live group already on top; order by nearest deadline within it.
      const ka = representativeKey(a, "deadline");
      const kb = representativeKey(b, "deadline");
      if (ka.bucket !== kb.bucket) return ka.bucket - kb.bucket;
      return ka.ms - kb.ms;
    }
    if (mode === "field") {
      const aKey = cardFieldSortKey(a);
      const bKey = cardFieldSortKey(b);
      const aEmpty = !aKey;
      const bEmpty = !bKey;
      if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
      const cmp = aKey.localeCompare(bKey);
      if (cmp !== 0) return cmp;
      return (a.title || "").localeCompare(b.title || "");
    }
    const ka = representativeKey(a, mode);
    const kb = representativeKey(b, mode);
    if (ka.bucket !== kb.bucket) return ka.bucket - kb.bucket;
    if (ka.ms !== kb.ms) return ka.ms - kb.ms;
    return (a.title || "").localeCompare(b.title || "");
  });
};

// Reminders feed: every live, not-yet-submitted entry with an upcoming (today
// or later) submission deadline. Returns flat items the Reminders panel uses.
export const conferenceReminderItems = (cards, now = todayStr()) => {
  const out = [];
  for (const card of cards || []) {
    for (const entry of card.entries || []) {
      if (!isEntryLive(entry)) continue;
      if (entry.submitted) continue;
      if (!entry.submissionDeadline) continue;
      if (entry.submissionDeadline < now) continue; // past -> "missed", not a reminder
      const label = paperTypeLabel(entry);
      out.push({
        cardId: card.id,
        entryId: entry.id,
        id: `${card.id}:${entry.id}`,
        text: `${card.title} ${entry.year || ""} — ${label} deadline`.replace(/\s+/g, " ").trim(),
        deadline: entry.submissionDeadline,
        completed: false,
      });
    }
  }
  return out;
};

// Remember feed: (1) entries flagged with remember=true (completable from Remember),
// and (2) submitted pending entries with a decision date (display-only).
export const conferenceRememberItems = (cards) => {
  const out = [];
  for (const card of cards || []) {
    for (const entry of card.entries || []) {
      if (entry.remember) {
        out.push({
          cardId: card.id,
          entryId: entry.id,
          id: `${card.id}:${entry.id}:remember`,
          kind: "remember",
          text: `${card.title} ${entry.year || ""}`.replace(/\s+/g, " ").trim(),
          completed: false,
          dueDate: entry.submissionDeadline || null,
        });
      }
      if (!entry.submitted) continue;
      if (entry.outcome && entry.outcome !== "pending") continue;
      if (!entry.decisionDate) continue;
      out.push({
        cardId: card.id,
        entryId: entry.id,
        id: `${card.id}:${entry.id}:decision`,
        kind: "decision",
        text: `${card.title} ${entry.year || ""} — decision expected`.replace(/\s+/g, " ").trim(),
        completed: false,
        dueDate: entry.decisionDate,
      });
    }
  }
  return out;
};

// Build a plain-text summary of a card for the "copy as text" button.
export const cardToText = (card) => {
  if (!card) return "";
  const lines = [];
  lines.push(card.title || "Untitled Conference");
  if ((card.organizers || "").trim()) lines.push(`Organizers: ${card.organizers}`);
  if ((card.titleExpanded || "").trim()) lines.push(`Full title: ${card.titleExpanded}`);
  for (const entry of card.entries || []) {
    lines.push("");
    lines.push(`— ${entry.year || ""} ${cardIsDead(card) ? "" : ""}`.trim());
    const status = deriveEntryStatus(entry);
    lines.push(`  Status: ${status.label}`);
    if ((entry.theme || "").trim()) lines.push(`  Theme: ${entry.theme}`);
    if ((entry.fields || []).length) lines.push(`  Field: ${entry.fields.join(", ")}`);
    if (entry.venue) lines.push(`  Venue: ${entry.venue}`);
    if (entry.dateFrom || entry.dateTo) {
      lines.push(`  Dates: ${entry.dateFrom || "?"}${entry.dateTo ? ` to ${entry.dateTo}` : ""}`);
    }
    if (entry.website) lines.push(`  Website: ${entry.website}`);
    if (entry.paperName) lines.push(`  Paper: ${entry.paperName} (${paperTypeLabel(entry)})`);
    if (entry.submissionDeadline) lines.push(`  Submission deadline: ${entry.submissionDeadline}`);
    if (entry.authors) lines.push(`  Authors: ${entry.authors}`);
    if (entry.decisionDate) lines.push(`  Decision date: ${entry.decisionDate}`);
    if ((entry.details || "").trim()) {
      lines.push(`  Details:`);
      for (const dl of entry.details.split("\n")) lines.push(`    ${dl}`);
    }
  }
  return lines.join("\n");
};

// Normalize an entry coming from storage so optional fields always exist.
export const normalizeEntry = (e = {}) => ({
  ...newEntry(e.year),
  ...e,
  id: e.id != null ? e.id : uid(),
  year: e.year != null ? String(e.year) : String(new Date().getFullYear()),
});
